import { formatCents, formatCount, tt } from '@agos/shared'

import type { BoardOrder } from './board'

/**
 * Saying who took a bar bill, before it is closed.
 *
 * **FB-075's gap, at its source.** `/summaries` answers the day's takings twice — a hero figure and the
 * per-employee wallets — and on 28 July the two differed by **42,85 €**, because some orders carry
 * neither a waiter nor a driver and so appear in the takings and in nobody's wallet. Measured across the
 * whole table: **32** such orders worth **683,50 €**, and **21 of them are bar**.
 *
 * Legacy's dispatch board already knows the fix and the port had not carried it. Closing a bar order
 * with no `employeeId` and no `driverId` does not close it — it opens a picker:
 *
 * ```ts
 * if (!order.employeeId && !order.driverId) setBarPickerOpen(true);
 * else onStatusChange(order.id, "delivered");
 * ```
 *
 * So the attribution is asked for at the one moment somebody knows the answer: the bill is being closed
 * and the person who served it is standing there. Asking later means asking a manager to guess in the
 * morning, which is how the 683,50 € got there.
 *
 * Assignment is `PATCH /orders/{orderId}` with `employeeId` — the same endpoint the driver goes through.
 * There are **3** waiters in this data.
 */

export interface WaiterLike {
  id?: number
  name?: string
  systemRole?: string
  active?: boolean
}

/** Who may be given a bill: a waiter who is still employed. */
export function assignableWaiters(employees: readonly WaiterLike[]): WaiterLike[] {
  return employees.filter(
    (employee) => employee.systemRole === 'waiter' && employee.active !== false && employee.id !== undefined,
  )
}

/**
 * A channel as much of it as this module needs: enough to say who owns the money.
 *
 * Structural rather than the generated `SalesChannel`, so a caller can pass whatever it has loaded -
 * the same shape `paymentNeedsWaiter` takes for the same reason.
 */
export interface ChannelLike {
  slug?: string
  onPremise?: boolean
}

/**
 * Whether this order is one somebody serves rather than drives - §9.391.
 *
 * <p><b>This read `type === 'bar' || type === 'restaurant'`, and that missed `na_sekeru`</b> - the
 * owner's own channel, and the one they reported stuck on this very screen: *"its in zoznam & archív
 * as kontrola kvality, but we cant move it."* §9.333 fixed two screens and this module has two more
 * copies of the same list.
 *
 * <p><b>The capability is `onPremise`, and it is the server's own.</b>
 * `SalesChannel.requiresHandoverOwner()` **is** `onPremise` - *somebody has to own the money; on a
 * delivery that is the driver, on the premises it is whoever was standing there* - and that is the
 * guard the close is trying to satisfy. Not `usesTables`, which answers a different question and is
 * what `paymentNeedsWaiter` correctly reads.
 *
 * <p>The hardcoded pair stays as the fallback for a caller with no channel list: both `bar` and
 * `restaurant` are `on_premise`, so the two answers agree and a screen that has not loaded its
 * channels behaves exactly as it did.
 */
export function isServedInHouse(order: BoardOrder, channels?: readonly ChannelLike[]): boolean {
  const type = order.type ?? ''
  const known = channels?.find((channel) => channel.slug === type)
  if (known !== undefined && known.onPremise !== undefined) return known.onPremise
  return type === 'bar' || type === 'restaurant'
}

export function isUnattributed(order: BoardOrder): boolean {
  return (order.employeeId ?? null) === null && (order.driverId ?? null) === null
}

/**
 * Whether closing this order has to ask who served it first.
 *
 * Only where nobody is on it and nobody drove it. An order already attributed closes straight away — the
 * question has an answer and re-asking it is how a rushed person picks the wrong name.
 */
export function mustAskWhoServed(order: BoardOrder, channels?: readonly ChannelLike[]): boolean {
  return isServedInHouse(order, channels) && isUnattributed(order)
}

export function assignPayload(waiterId: number): Record<string, unknown> {
  return { employeeId: waiterId }
}

/** Said in the picker, so the reason is on the screen rather than in a ledger nobody reads. */
export function askNote(order: BoardOrder): string {
  return `#${order.orderNumber ?? order.id ?? ''} - ${tt('kto ju obsluhoval? Bez toho sa tržba nedostane do žiadnej peňaženky a večerná uzávierka nebude sedieť.')}`
}

/** Said on the board where unattributed orders are sitting, with what they are worth. */
export function unattributedNote(
  orders: readonly BoardOrder[],
  channels?: readonly ChannelLike[],
): string {
  // `filter(mustAskWhoServed)` would hand the helper the index as its second argument, which is how
  // an optional parameter quietly becomes a bug. Named, deliberately.
  const open = orders.filter((order) => mustAskWhoServed(order, channels))
  if (open.length === 0) return ''
  const worth = open.reduce((total, order) => {
    const value = typeof order.total === 'number' ? order.total : Number(order.total ?? 0)
    return total + (Number.isFinite(value) ? Math.round(value * 100) : 0)
  }, 0)
  return `${formatCount(open.length)} ${tt('bez obsluhy')} (${formatCents(worth)}) - ${tt('pri zatváraní sa spýtame, kto ich mal.')}`
}

/** Said when there is nobody to pick, because an empty list reads as a broken screen. */
export function noWaitersNote(): string {
  return tt('Nikto s rolou čašník nie je aktívny - priradenie sa nedá urobiť odtiaľto.')
}

/**
 * Why the order was closed, for the status log - §9.342.
 *
 * **Not an invented reason**, which is the bar §9.341 set: `reason` is `@NotBlank` and a default
 * would be a fiction in an audit trail. This is the datum the dialog exists to collect - *who served
 * it* - written into the field that records why the status changed. The old plain `PATCH` wrote
 * nothing at all.
 *
 * Falls back to the id because an empty reason is refused by the server, and it would be refused
 * *after* the dialog had already assigned somebody.
 */
export function closeReasonFor(waiter: WaiterLike): string {
  const who = waiter.name ?? `#${waiter.id ?? ''}`
  return `${tt('Uzavreté na pokladni - obsluha')} ${who}`
}
